import { Map } from 'lucide-react';
import { Button, CardHeader, CardTitle, Table, TableBody, TableCell, TableRow } from "randmarcomps";
import React from "react";
import { Link, useParams } from "react-router-dom";
import Path from "../../routes/Path";
import { GeneralDocument as LegacyGeneralDocument } from "../../stores/document/Document";
import { GeneralDocument as ApiGeneralDocument } from "randmar-api-client";
import { FormatDateFromStringWithHoursFromUTC } from "../util/DateTimeUtil";

interface IProps {
  generalDocument: LegacyGeneralDocument | ApiGeneralDocument;
}

const isLegacy = (doc: LegacyGeneralDocument | ApiGeneralDocument): doc is LegacyGeneralDocument => 'pONumber' in doc;

type NormalizedGeneralDocument = {
  OrderDate?: string;
  PONumber?: string;
  BidNumber?: string | null;
  EnteredBy?: string | null;
  ShipName?: string;
  ShipAddress1?: string;
  ShipAddress2?: string | null;
  ShipCity?: string;
  ShipProvince?: string;
  ShipZip?: string;
  ShipCountry?: string;
};

const GeneralDocumentSummary: React.FC<IProps> = ({ generalDocument: rawDocument }) => {
  const { applicationId } = useParams<{ applicationId: string }>();

  const document = React.useMemo<NormalizedGeneralDocument>(() => {
    if (isLegacy(rawDocument)) {
      return {
        OrderDate: rawDocument.orderDate,
        PONumber: rawDocument.pONumber,
        BidNumber: rawDocument.bidNumber,
        EnteredBy: rawDocument.enteredBy,
        ShipName: rawDocument.shipName,
        ShipAddress1: rawDocument.shipAddress1,
        ShipAddress2: rawDocument.shipAddress2,
        ShipCity: rawDocument.shipCity,
        ShipProvince: rawDocument.shipProvince,
        ShipZip: rawDocument.shipZip,
        ShipCountry: rawDocument.shipCountry,
      } as NormalizedGeneralDocument;
    }
    return rawDocument as NormalizedGeneralDocument;
  }, [rawDocument]);

  const googleMapsUrl = React.useMemo(() => {
    const addressParts = [
      document.ShipAddress1,
      document.ShipAddress2,
      document.ShipCity,
      document.ShipProvince,
      document.ShipZip,
      document.ShipCountry,
    ].filter(Boolean);
    const query = addressParts.join(" ");
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  }, [document.ShipAddress1, document.ShipAddress2, document.ShipCity, document.ShipProvince, document.ShipZip, document.ShipCountry]);

  return (
    <div className="flex flex-col md:flex-row justify-evenly w-full space-y-4 md:space-y-0 md:space-x-4">
      <div className="border border-purple-700 rounded-lg h-full">
        <Table>
          <TableBody>
            <TableRow className="hover:bg-purple-50">
              <TableCell className="border-purple-700 rounded-tl-lg font-bold">Order Date</TableCell>
              <TableCell className="border-purple-700 rounded-tr-lg">{FormatDateFromStringWithHoursFromUTC(document.OrderDate)}</TableCell>
            </TableRow>
            <TableRow className="hover:bg-purple-50">
              <TableCell className="border-purple-700 font-bold">PO Number</TableCell>
              <TableCell className="border-purple-700">{document.PONumber}</TableCell>
            </TableRow>
            {document.BidNumber !== null &&
              <TableRow className="hover:bg-purple-50">
                <TableCell className="border-purple-700 font-bold">Opportunity Number</TableCell>
                <TableCell className="border-purple-700">
                  <Link to={Path.opportunity(applicationId, document.BidNumber)}>{document.BidNumber}</Link>
                </TableCell>
              </TableRow>
            }
            <TableRow className="hover:bg-purple-50">
              <TableCell className="border-none rounded-bl-lg font-bold">Entered by</TableCell>
              <TableCell className="border-none rounded-br-lg">
                {document.EnteredBy ?? <>&#x2622;&#xfe0f;</>}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-col space-y-2">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Shipping Address</CardTitle>
        </CardHeader>
        <div className="flex flex-col space-y-1 justify-center text-center">
          <div className="font-bold">{document.ShipName}</div>
          <div>{document.ShipAddress1}</div>
          {document.ShipAddress2 && <div>{document.ShipAddress2}</div>}
          <div>{document.ShipCity}, {document.ShipProvince}</div>
          <div>{document.ShipZip} ({document.ShipCountry})</div>
      </div>
        <a title="Google Maps" target="_blank" rel="noreferrer noopener" className="no-underline"
          href={googleMapsUrl}>
          <Button variant="default" className="w-full">
            <Map className="mr-2" />
            Maps
          </Button>
        </a>
      </div>
    </div>
  );
}

export default GeneralDocumentSummary;
